import { Component, effect, ElementRef, inject, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ExportImportService } from '../../services/export-import.service';
import { CanvasService } from '../../services/canvas.service';

interface GalleryItem {
    id: string;
    name: string;
    category?: string;
    url: string;
    tags?: string[];
}

interface GalleryFolder {
    id: string;
    name: string;
    icon: string;
    items: GalleryItem[];
    subfolders?: GallerySubfolder[];
}

interface GallerySubfolder {
    id: string;
    name: string;
    items: GalleryItem[];
}

interface GalleryData {
    folders: GalleryFolder[];
    items?: GalleryItem[];
}

@Component({
    selector: 'app-gallery-panel',
    imports: [CommonModule],
    templateUrl: './gallery-panel.component.html',
    styleUrl: './gallery-panel.component.scss'
})
export class GalleryPanelComponent {
    @ViewChild('svgUploadInput') svgUploadInput!: ElementRef<HTMLInputElement>;
    private exportImportService = inject(ExportImportService);
    private canvasService = inject(CanvasService);
    private galleryData = signal<GalleryData | null>(null);
    readonly currentPathSignal = signal<Array<{ id: string; name: string }>>([]);

    readonly currentFolders = signal<GalleryFolder[]>([]);
    readonly currentItems = signal<GalleryItem[]>([]);
    readonly currentPath = this.currentPathSignal.asReadonly();

    constructor() {
        effect(() => {
            this.loadGalleryData();
        });

        effect(() => {
            this.updateCurrentContent();
        });
    }

    private async loadGalleryData(): Promise<void> {
        try {
            const url = new URL('gallery-data.json', document.baseURI).toString();
            const response = await fetch(url, { cache: 'no-store' });
            const data: GalleryData = await response.json();
            this.galleryData.set(data);
            this.updateCurrentContent();
        } catch (error) {
            console.error('Failed to load gallery data:', error);
            this.galleryData.set({ folders: [] });
            this.updateCurrentContent();
        }
    }

    private updateCurrentContent(): void {
        const data = this.galleryData();
        if (!data) return;

        const path = this.currentPathSignal();

        if (path.length === 0) {
            // Show root folders and root items
            this.currentFolders.set(data.folders);
            this.currentItems.set(data.items || []);
        } else {
            // Navigate through folders
            let currentLevel: any = data.folders.find(f => f.id === path[0].id);

            for (let i = 1; i < path.length; i++) {
                if (currentLevel.subfolders) {
                    currentLevel = currentLevel.subfolders.find((sf: any) => sf.id === path[i].id);
                }
            }

            if (currentLevel?.subfolders) {
                this.currentFolders.set(
                    currentLevel.subfolders.map((sf: GallerySubfolder) => ({
                        ...sf,
                        icon: '📁',
                        items: sf.items || []
                    } as GalleryFolder))
                );
            } else {
                this.currentFolders.set([]);
            }

            this.currentItems.set(currentLevel?.items || []);
        }
    }

    navigateToFolder(folder: GalleryFolder): void {
        const newPath = [...this.currentPathSignal(), { id: folder.id, name: folder.name }];
        this.currentPathSignal.set(newPath);
    }

    goBack(): void {
        const path = [...this.currentPathSignal()];
        path.pop();
        this.currentPathSignal.set(path);
    }

    navigateToRoot(): void {
        this.currentPathSignal.set([]);
    }

    onDragStart(event: DragEvent, item: GalleryItem): void {
        if (!event.dataTransfer) return;
        const url = new URL(item.url, window.location.origin).href;
        event.dataTransfer.setData('text/plain', url);
        event.dataTransfer.setData('text/uri-list', url);
        event.dataTransfer.effectAllowed = 'copy';
    }

    triggerUpload(): void {
        this.svgUploadInput?.nativeElement.click();
    }

    async onSvgSelected(event: Event): Promise<void> {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) return;

        try {
            await this.exportImportService.importSVG(file);
        } catch (error) {
            console.error('Failed to import SVG:', error);
            alert('Failed to import SVG');
        } finally {
            input.value = '';
        }
    }

    async addItemToCanvas(item: GalleryItem): Promise<void> {
        try {
            const url = new URL(item.url, window.location.origin).href;
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Failed to fetch SVG: ${response.status}`);
            }
            const blob = await response.blob();
            const file = new File([blob], `${item.id}.svg`, { type: 'image/svg+xml' });
            await this.exportImportService.importSVG(file);
        } catch (error) {
            console.error('Failed to add SVG item:', error);
            alert('Failed to add SVG item');
        }
    }

    isVisitingCard(): boolean {
        return this.canvasService.canvasSizeTypeValue() === 'visiting-card';
    }

    isEnvelope(): boolean {
        return this.canvasService.canvasSizeTypeValue() === 'cover-9-5x4-25';
    }

    isLetterhead(): boolean {
        return this.canvasService.canvasSizeTypeValue() === 'letterhead-a4';
    }

    private resetCanvasView(): void {
        this.canvasService.setZoom(1);
        this.canvasService.setPan({ x: 0, y: 0 });
    }

    async addVisitingCardAsset(side: 'front' | 'back'): Promise<void> {
        this.resetCanvasView();
        this.canvasService.setActiveSide(side);

        try {
            const sampleName = side === 'front' ? 'vc-ai-sample-front' : 'vc-ai-sample-back';
            const url = new URL(`/assets/visiting-card-sample/${sampleName}.svg`, window.location.origin).href;
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Failed to fetch visiting card SVG (${response.status})`);
            }
            const blob = await response.blob();
            const file = new File([blob], `visiting-card-${side}.svg`, { type: 'image/svg+xml' });
            const id = await this.exportImportService.importSVG(file);
            this.canvasService.updateObject(id, { side });
            this.canvasService.selectObject(id);
        } catch (error) {
            console.error('Failed to add visiting card SVG:', error);
            alert('Failed to add visiting card SVG');
        }
    }

    async addAssetSvg(assetPath: string): Promise<void> {
        this.resetCanvasView();

        try {
            const url = new URL(assetPath, window.location.origin).href;
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Failed to fetch SVG (${response.status})`);
            }
            const blob = await response.blob();
            const file = new File([blob], assetPath.split('/').pop() || 'asset.svg', { type: 'image/svg+xml' });
            await this.exportImportService.importSVG(file);
        } catch (error) {
            console.error('Failed to add SVG asset:', error);
            alert('Failed to add SVG asset');
        }
    }
}
